import { Spinner } from '../ui/Skeleton';

const BUTTON_CLASS =
  'h-9 min-w-9 rounded border border-[color:var(--color-line)] px-3 text-sm font-medium disabled:opacity-40 enabled:hover:bg-black/5 dark:enabled:hover:bg-white/10';

/**
 * Server-side pagination (FR-5 — Or's real project holds 486 items, and 486 rows in the DOM is the
 * reason lists like this feel slow).
 *
 * ⭐ It reads "51–100 of 486" rather than "page 2 of 10": the position in the list is the thing
 * being scanned, and the page number is an artefact of how it was fetched.
 */
export function Pagination({
  page,
  perPage,
  total,
  busy,
  note,
  onPageChange,
}: {
  page: number;
  perPage: number;
  total: number;
  busy: boolean;
  /**
   * ⚠️ Where the missing rows went. The range counts what the SERVER returned; nesting draws some of
   * those rows under a parent, so the two numbers legitimately differ and the difference is stated
   * rather than left for the reader to notice and distrust.
   */
  note?: string | null;
  onPageChange: (page: number) => void;
}) {
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  const first = total === 0 ? 0 : (page - 1) * perPage + 1;
  const last = Math.min(page * perPage, total);

  return (
    <div className="safe-bottom sticky bottom-0 flex flex-wrap items-center justify-between gap-2 border-t border-[color:var(--color-line)] bg-[color:var(--color-surface)] px-3 py-2 sm:px-4">
      <p className="text-sm text-[color:var(--color-ink-muted)]">
        {total === 0 ? 'No items' : `${first}–${last} of ${total}`}
        {note ? <span className="text-[color:var(--color-ink-subtle)]"> · {note}</span> : null}
        {busy ? (
          <>
            {' '}
            <Spinner label="Loading" />
          </>
        ) : null}
      </p>
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          className={BUTTON_CLASS}
          disabled={page <= 1 || busy}
          onClick={() => onPageChange(page - 1)}
        >
          ← Prev
        </button>
        <span className="px-1 text-sm text-[color:var(--color-ink-muted)] tabular-nums">
          {page} / {lastPage}
        </span>
        <button
          type="button"
          className={BUTTON_CLASS}
          disabled={page >= lastPage || busy}
          onClick={() => onPageChange(page + 1)}
        >
          Next →
        </button>
      </div>
    </div>
  );
}
